import { useEffect, useState } from 'react';
import { burn } from './burn';

// A page for e2e/ambient.spec.ts, at #ambient and not in the lab's list: renders that a resize, a media
// query, a hover or a scroll causes, each heavy, beside a quick button. None of them is the button's work.
// The tap card is the other way round: a finger tapping its button enters the card first, so the card's
// render is the tap's own (e2e/phone.spec.ts).

const CELLS = 400;

function Cell({ label }: { label: string }) {
  burn(0.25);
  return <li>{label}</li>;
}

function Cells({ label, name }: { label: string; name: string }) {
  return (
    <ul className="grid" data-test={name} data-label={label}>
      {Array.from({ length: CELLS }, (_, i) => (
        <Cell key={i} label={`${label} ${i}`} />
      ))}
    </ul>
  );
}

/** Whether the media query matches, kept current by its `change` event, as useMediaQuery hooks do. React 17 has no useSyncExternalStore. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const list = matchMedia(query);
    const on = () => setMatches(list.matches);
    list.addEventListener('change', on);
    return () => list.removeEventListener('change', on);
  }, [query]);
  return matches;
}

function BreakpointGrid() {
  const wide = useMediaQuery('(min-width: 600px)');
  return <Cells name="breakpoint" label={wide ? 'wide' : 'narrow'} />;
}

/** Whether the window is 600 px wide or more, kept current by `resize`, as useWindowSize hooks do. */
function WidthGrid() {
  const [wide, setWide] = useState(() => innerWidth >= 600);
  useEffect(() => {
    const on = () => setWide(innerWidth >= 600);
    addEventListener('resize', on);
    return () => removeEventListener('resize', on);
  }, []);
  return <Cells name="width" label={wide ? 'wide' : 'narrow'} />;
}

function HoverCard() {
  const [open, setOpen] = useState(false);
  return (
    <div data-test="hover-card" onMouseEnter={() => setOpen(true)} style={{ padding: 8, border: '1px solid' }}>
      Hover me
      {open ? <Cells name="hover" label="open" /> : null}
    </div>
  );
}

function ScrollList() {
  const [scrolled, setScrolled] = useState(false);
  return (
    <div data-test="scroll-list" onScroll={() => setScrolled(true)} style={{ height: 120, overflow: 'auto', border: '1px solid' }}>
      <div style={{ height: 1200 }}>{scrolled ? <Cells name="scroll" label="scrolled" /> : 'Scroll me'}</div>
    </div>
  );
}

/** A quick button: its click renders the button alone. */
function TapButton() {
  const [taps, setTaps] = useState(0);
  return (
    <button data-test="tap" onClick={() => setTaps((n) => n + 1)}>
      Tap ({taps})
    </button>
  );
}

/** A card that opens its 400 cells when a pointer enters it, around the quick button. */
function TapCard() {
  const [open, setOpen] = useState(false);
  return (
    <div data-test="tap-card" onPointerEnter={() => setOpen(true)} style={{ padding: 8, border: '1px solid' }}>
      <TapButton />
      {open ? <Cells name="tap-cells" label="tapped" /> : null}
    </div>
  );
}

/** The quick click: its render is this button alone. */
function SaveButton() {
  const [saved, setSaved] = useState(0);
  return (
    <button className="primary" data-test="trigger" onClick={() => setSaved((n) => n + 1)}>
      Save ({saved})
    </button>
  );
}

export function Ambient() {
  return (
    <main>
      <SaveButton />
      <TapCard />
      <HoverCard />
      <ScrollList />
      <BreakpointGrid />
      <WidthGrid />
    </main>
  );
}
